import axios from 'axios';
import { toast } from 'react-toastify';
import { refreshAccessToken, isTokenExpired } from 'services/tokenRefresh.service';

const baseApi = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  //withCredentials: true,
});

baseApi.interceptors.request.use(
  async (config) => {
    // Skip auth handling if we're already on login page
    const isLoginPage = window.location.pathname === '/login';

    let accessToken = localStorage.getItem('accessToken');

    // Proactively refresh token if it's expired or about to expire
    if (accessToken && isTokenExpired(accessToken) && !isLoginPage) {
      try {
        accessToken = await refreshAccessToken();
      } catch (err) {
        // Clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        toast.error('Your session has expired. Please log in again.');
        if (!isLoginPage) {
          window.location.href = '/login';
        }
        throw err;
      }
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

baseApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isLoginPage = window.location.pathname === '/login';

    // If we get 401 and haven't already retried and we're not on login page
    if (error.response?.status === 401 && !originalRequest._retry && !isLoginPage) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return await baseApi(originalRequest);
      } catch (refreshError) {
        // Clear tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // Show user-friendly message only if not already on login page
        if (!isLoginPage) {
          toast.error('Your session has expired. Please log in again.');
          // Redirect to login
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default baseApi;
